import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BarChart3, BookOpen, Calendar, Award, Filter, TrendingUp, Target, Star, MoreVertical, TrendingDown, Minus, Percent } from "lucide-react";
import { getApiUrl } from "../../config/api";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";
import { useActiveLearner } from "../../hooks/useActiveLearner";
import { getStudentAuthHeaders } from "../../utils/studentSession";

interface TestResult {
  id: string;
  score: number;
  test: {
    id: string;
    title: string;
    date: string;
    subject: { name: string; };
  };
}

const MyGrades: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const { activeLearnerId } = useActiveLearner();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<TestResult[]>([]);
  const [selectedTerm, setSelectedTerm] = useState<string>("all");
  const [selectedYear, setSelectedYear] = useState<string>("all");
  const [terms, setTerms] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [stats, setStats] = useState({ average: 0, highest: 0, lowest: 0, totalTests: 0 });

  const fetchResults = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get<TestResult[]>(getApiUrl("assessments/results/student"), {
        headers: getStudentAuthHeaders(token)
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setResults(data);

      const uniqueTerms = Array.from(new Set(data.map(r => r.test.title))).sort();
      setTerms(uniqueTerms);

      const uniqueYears = Array.from(new Set(data.map(r => new Date(r.test.date).getFullYear().toString()))).sort((a, b) => parseInt(b) - parseInt(a));
      setYears(uniqueYears);

      if (data.length > 0) {
        const scores = data.map(r => r.score);
        setStats({
          average: Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10,
          highest: Math.max(...scores),
          lowest: Math.min(...scores),
          totalTests: data.length
        });
      }
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, [token, activeLearnerId]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const filteredResults = results.filter(r => {
    const termMatch = selectedTerm === "all" || r.test.title === selectedTerm;
    const yearMatch = selectedYear === "all" || new Date(r.test.date).getFullYear().toString() === selectedYear;
    return termMatch && yearMatch;
  });

  const getGradeStyle = (score: number) => {
    if (score >= 90) return { color: 'text-teal-600', badge: 'bg-teal-50 border-teal-100', text: 'A+' };
    if (score >= 80) return { color: 'text-blue-600', badge: 'bg-blue-50 border-blue-100', text: 'A' };
    if (score >= 70) return { color: 'text-indigo-600', badge: 'bg-indigo-50 border-indigo-100', text: 'B' };
    if (score >= 60) return { color: 'text-amber-600', badge: 'bg-amber-50 border-amber-100', text: 'C' };
    return { color: 'text-red-500', badge: 'bg-red-50 border-red-100', text: 'F' };
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="page-title-section">
          <div>
            <h1>Academic Performance</h1>
            <p className="page-subtitle">Personalized dashboard of examination outcomes and grade distribution</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-8 pt-0">
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center mb-4"><TrendingUp size={20} /></div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Global GPA</p>
            <h4 className="text-2xl font-black text-slate-800">{stats.average}%</h4>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-4"><Star size={20} /></div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Peak Result</p>
            <h4 className="text-2xl font-black text-slate-800 text-teal-600">{stats.highest}%</h4>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center mb-4"><Target size={20} /></div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Base Reference</p>
            <h4 className="text-2xl font-black text-slate-800">{stats.lowest}%</h4>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
            <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center mb-4"><Award size={20} /></div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Evaluations</p>
            <h4 className="text-2xl font-black text-slate-800">{stats.totalTests}</h4>
          </div>
        </div>

        <div className="utopia-page-container">
          <div className="utopia-table-header p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h2>Outcome Ledger</h2>
              <p className="text-xs text-slate-400 mt-1">Verified examination results from the school registry</p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-slate-400" />
                <select className="utopia-input h-14 text-sm font-bold w-48 min-w-[180px]" value={selectedTerm} onChange={e => setSelectedTerm(e.target.value)}>
                  <option value="all">All Cycles</option>
                  {terms.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <select className="utopia-input h-14 text-sm font-bold w-40 min-w-[160px]" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
                <option value="all">All Years</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="p-32 text-center text-slate-200"><TrendingUp size={40} className="animate-pulse mx-auto opacity-20" /></div>
          ) : filteredResults.length === 0 ? (
            <div className="p-32 text-center text-slate-300">
              <Target size={64} className="mx-auto mb-4 opacity-5" />
              <p className="font-medium">No outcome data matched the current filtering parameters.</p>
            </div>
          ) : (
            <table className="utopia-table group/table">
              <thead>
                <tr>
                  <th>Examination Details</th>
                  <th>Subject Information</th>
                  <th>Assessment Date</th>
                  <th className="text-center">Score Breakdown</th>
                  <th className="text-center">Grade & Performance</th>
                  <th className="text-center">Trend</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((r, index) => {
                  const st = getGradeStyle(r.score);
                  const testDate = new Date(r.test.date);
                  const previousScore = index > 0 ? filteredResults[index - 1].score : null;
                  const scoreDiff = previousScore !== null ? r.score - previousScore : null;
                  const percentile = filteredResults.filter(res => res.score <= r.score).length / filteredResults.length * 100;

                  return (
                    <tr key={r.id} className="group/row">
                      <td>
                        <div className="utopia-info-cell">
                          <span className="utopia-table-cell-primary">{r.test.title}</span>
                          <div className="utopia-info-row mt-1">
                            <Calendar size={10} className="text-slate-400" />
                            <span className="utopia-table-cell-meta">
                              {testDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="utopia-info-cell">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-3 h-3 rounded-full bg-teal-400 group-hover/row:bg-teal-500 transition-colors"></div>
                            <span className="utopia-table-cell-primary">{r.test.subject.name}</span>
                          </div>
                          <div className="utopia-info-row ml-5">
                            <BookOpen size={10} className="text-slate-400" />
                            <span className="utopia-table-cell-meta">Subject Assessment</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="utopia-info-cell">
                          <div className="utopia-info-row">
                            <Calendar size={12} className="text-slate-400" />
                            <span className="utopia-table-cell-primary">
                              {testDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          <div className="utopia-info-row mt-1">
                            <span className="utopia-table-cell-secondary">
                              {testDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="flex flex-col items-center">
                          <span className={`${st.color} font-black text-xl mb-1`}>{r.score}%</span>
                          <div className="utopia-progress-bar" style={{ width: '80px' }}>
                            <div className="utopia-progress-fill" style={{ width: `${r.score}%` }}></div>
                          </div>
                          <div className="utopia-info-row mt-2">
                            <Percent size={10} className="text-slate-400" />
                            <span className="utopia-table-cell-meta">
                              {percentile.toFixed(0)}th percentile
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="flex flex-col items-center">
                          <span className={`utopia-badge ${st.badge} px-5 py-2 text-[10px] font-black uppercase tracking-widest mb-2`}>
                            {st.text} Class
                          </span>
                          <div className="utopia-info-row">
                            <Award size={10} className={st.color.replace('text-', 'text-')} />
                            <span className={`utopia-table-cell-secondary ${st.color}`}>
                              {r.score >= 90 ? 'Excellent' : r.score >= 80 ? 'Good' : r.score >= 70 ? 'Average' : r.score >= 60 ? 'Below Average' : 'Needs Improvement'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        {scoreDiff !== null ? (
                          <div className="flex flex-col items-center">
                            {scoreDiff > 0 ? (
                              <>
                                <TrendingUp size={18} className="text-teal-600 mb-1" />
                                <span className="utopia-table-cell-primary text-teal-600 font-bold text-xs">
                                  +{scoreDiff.toFixed(1)}%
                                </span>
                                <span className="utopia-table-cell-meta">Improved</span>
                              </>
                            ) : scoreDiff < 0 ? (
                              <>
                                <TrendingDown size={18} className="text-red-500 mb-1" />
                                <span className="utopia-table-cell-primary text-red-500 font-bold text-xs">
                                  {scoreDiff.toFixed(1)}%
                                </span>
                                <span className="utopia-table-cell-meta">Declined</span>
                              </>
                            ) : (
                              <>
                                <Minus size={18} className="text-slate-400 mb-1" />
                                <span className="utopia-table-cell-secondary text-xs">No change</span>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="utopia-info-cell">
                            <span className="utopia-table-cell-meta">First record</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default MyGrades;










